import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
  Send,
  ArrowLeft,
  CheckCircle,
  Package,
  User,
  ShieldCheck,
  Clock,
  ChevronRight,
  Plus,
  Trash2,
  ShoppingBag,
  Truck,
  FileText,
  Check,
  X,
  AlertCircle,
  Tag,
  Search,
  Loader2,
  FolderTree,
  Utensils,
  SprayCan,
  Mail,
  RefreshCw,
} from "lucide-react";
import { requestService } from "../api/services/requestService";
import { productService } from "../api/services/productService";
import { userService } from "../api/services/userService";
import { useToast } from "../components/common/ThemeToaster";

// Product Image Component
const ProductImage = ({ product, className }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    const loadImage = () => {
      if (
        product?.mainImagePath &&
        (product.mainImagePath.startsWith("http://") ||
          product.mainImagePath.startsWith("https://"))
      ) {
        setImageUrl(product.mainImagePath);
        setImageLoading(false);
        return;
      }

      if (product?.mainImagePath) {
        const apiBaseUrl =
          import.meta.env.VITE_API_UPLAODS || "http://localhost:3000";
        setImageUrl(`${apiBaseUrl}${product.mainImagePath}`);
        setImageLoading(false);
        return;
      }

      if (product?.galleryImages && product.galleryImages.length > 0) {
        const firstImage = product.galleryImages[0];

        if (
          firstImage?.path &&
          (firstImage.path.startsWith("http://") ||
            firstImage.path.startsWith("https://"))
        ) {
          setImageUrl(firstImage.path);
          setImageLoading(false);
          return;
        }

        if (firstImage?.path) {
          const apiBaseUrl =
            import.meta.env.VITE_API_UPLAODS || "http://localhost:3000";
          setImageUrl(`${apiBaseUrl}${firstImage.path}`);
          setImageLoading(false);
          return;
        }

        if (firstImage?.data) {
          setImageUrl(`data:${firstImage.mimeType};base64,${firstImage.data}`);
          setImageLoading(false);
          return;
        }
      }

      if (
        product?.image &&
        (product.image.startsWith("http://") ||
          product.image.startsWith("https://"))
      ) {
        setImageUrl(product.image);
        setImageLoading(false);
        return;
      }

      setImageLoading(false);
    };

    loadImage();
  }, [product]);

  if (imageLoading && !imageError) {
    return (
      <div className={`flex items-center justify-center ${className}`}>
        <Loader2 size={20} className="animate-spin text-primary-500" />
      </div>
    );
  }

  if (imageUrl && !imageError) {
    return (
      <img
        src={imageUrl}
        alt={product?.imageAlt || product?.name || "Product image"}
        className={`object-cover ${className}`}
        onError={() => {
          console.error(`Failed to load product image: ${imageUrl}`);
          setImageError(true);
        }}
      />
    );
  }

  return (
    <div
      className={`flex items-center justify-center ${className} bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900`}
    >
      <Package size={24} className="text-gray-400" />
    </div>
  );
};

// Product Card Component for the product list
const ProductCard = ({ product, onSelect }) => {
  return (
    <button
      onClick={() => onSelect(product)}
      className="w-full px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-3"
    >
      <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-800 overflow-hidden flex-shrink-0">
        <ProductImage product={product} className="w-full h-full" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
          {product.name}
        </p>
        <p className="text-xs text-gray-500 truncate">SKU: {product.sku}</p>
      </div>
      <Tag className="w-3 h-3 text-gray-400 flex-shrink-0" />
    </button>
  );
};

const Inquiry = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    success: toastSuccess,
    error: toastError,
    info: toastInfo,
  } = useToast();

  const {
    isAuthenticated,
    user,
    isLoading: authLoading,
  } = useSelector((state) => state.auth);

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submittedData, setSubmittedData] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [isResendingVerification, setIsResendingVerification] = useState(false);

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryProducts, setCategoryProducts] = useState([]);
  const [categoryLoading, setCategoryLoading] = useState(false);

  const [allProducts, setAllProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [productSearch, setProductSearch] = useState("");
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [selectedProductIndex, setSelectedProductIndex] = useState(null);
  const [urlProductProcessed, setUrlProductProcessed] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    country: "",
    city: "",
    address: "",
    postalCode: "",
    products: [],
    message: "",
    requiredDeliveryDate: "",
    estimatedValue: "",
  });

  const categories = [
    {
      id: "food",
      name: "Food & Beverages",
      icon: Utensils,
      description:
        "Premium quality food products sourced globally from trusted suppliers.",
      gradient: "from-amber-500 to-orange-500",
    },
    {
      id: "non-food",
      name: "Non-Food Products",
      icon: SprayCan,
      description:
        "Industrial supplies, consumer goods, electronics, and more.",
      gradient: "from-blue-500 to-cyan-500",
    },
  ];

  // Check authentication and redirect if not logged in
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate("/user/login", {
        state: {
          from: location.pathname,
          message: "Please log in to submit an request",
        },
      });
    }
  }, [isAuthenticated, authLoading, navigate, location]);

  // Pre-fill form with user data when authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      setFormData((prev) => ({
        ...prev,
        name:
          user.firstName && user.lastName
            ? `${user.firstName} ${user.lastName}`
            : user.name || user.email?.split("@")[0] || "",
        email: user.email || "",
        phone: user.phone || "",
        company: user.company || "",
        country: user.country || "",
        city: user.city || "",
        address: user.address || "",
        postalCode: user.postalCode || "",
      }));
    }
  }, [isAuthenticated, user]);

  // Fetch all products from API
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setProductsLoading(true);
        const response = await productService.getAllProducts({
          limit: 100,
          isAvailable: true,
        });

        let productsData = [];
        if (response.data?.data?.products) {
          productsData = response.data.data.products;
        } else if (response.data?.products) {
          productsData = response.data.products;
        } else if (response.products) {
          productsData = response.products;
        } else if (Array.isArray(response.data)) {
          productsData = response.data;
        } else if (Array.isArray(response)) {
          productsData = response;
        }

        setAllProducts(productsData);
      } catch (err) {
        console.error("Error fetching products:", err);
        toastError("Failed to load products. Please refresh the page.");
      } finally {
        setProductsLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Fetch products by category when selected
  useEffect(() => {
    if (selectedCategory) {
      fetchCategoryProducts(selectedCategory);
    } else {
      setCategoryProducts([]);
    }
  }, [selectedCategory]);

  const fetchCategoryProducts = async (category) => {
    setCategoryLoading(true);
    try {
      const filteredProducts = allProducts.filter(
        (p) => p.category === category,
      );
      setCategoryProducts(filteredProducts);
    } catch (err) {
      console.error("Error filtering products:", err);
      toastError("Failed to load category products");
    } finally {
      setCategoryLoading(false);
    }
  };

  // Handle product from URL parameter and auto-select category
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const productId = params.get("productId");

    if (productId && allProducts.length > 0 && !urlProductProcessed) {
      const product = allProducts.find((p) => p.id === parseInt(productId));
      if (product) {
        if (product.category && !selectedCategory) {
          setSelectedCategory(product.category);
          toastInfo(
            `Category "${categories.find((c) => c.id === product.category)?.name}" selected based on product`,
          );
        }

        if (!formData.products.some((p) => p.id === product.id)) {
          setFormData((prev) => ({
            ...prev,
            products: [
              {
                id: product.id,
                name: product.name,
                sku: product.sku,
                mainImagePath: product.mainImagePath,
                imageBase64: product.imageBase64,
                galleryImages: product.galleryImages,
                image: product.image,
                imageAlt: product.imageAlt,
                category: product.category,
                notes: "",
              },
            ],
          }));
          toastSuccess(`Added "${product.name}" to your request`);

          if (product.category) {
            setTimeout(() => {
              setCurrentStep(2);
            }, 500);
          }
        }
      }
      setUrlProductProcessed(true);
    }
  }, [
    location.search,
    allProducts,
    formData.products,
    selectedCategory,
    urlProductProcessed,
  ]);

  const isTokenValid = () => {
    const token = localStorage.getItem("accessToken");
    if (!token) return false;

    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const expirationTime = payload.exp * 1000;
      const currentTime = Date.now();

      if (currentTime >= expirationTime) {
        console.log("Token expired");
        return false;
      }
      return true;
    } catch (e) {
      console.error("Error checking token:", e);
      return false;
    }
  };

  const handleResendVerification = async () => {
    setIsResendingVerification(true);
    try {
      await userService.resendVerificationEmail();
      toastSuccess("Verification email sent successfully!");
    } catch (err) {
      console.error("Error resending verification:", err);
      toastError(
        err?.response?.data?.message || "Failed to resend verification email",
      );
    } finally {
      setIsResendingVerification(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isAuthenticated) {
      navigate("/user/login", {
        state: {
          from: location.pathname,
          message: "Please log in to submit your request",
        },
      });
      return;
    }

    if (!isTokenValid()) {
      toastError("Your session has expired. Please log in again.");
      localStorage.clear();
      navigate("/user/login");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      if (formData.products.length === 0) {
        throw new Error("Please add at least one product to your request");
      }

      const productsArray = formData.products.map((p) => ({
        productId: p.id,
        productName: p.name,
        notes: p.notes || "",
      }));

      const requestData = {
        message:
          formData.message ||
          `Request for ${formData.products.length} product(s)`,
        subject: `Product Request - ${formData.products.length} items`,
        deliveryAddress: formData.address,
        deliveryCity: formData.city,
        deliveryCountry: formData.country,
        deliveryPostalCode: formData.postalCode,
        requiredDeliveryDate: formData.requiredDeliveryDate || undefined,
        estimatedValue: formData.estimatedValue
          ? parseFloat(formData.estimatedValue)
          : undefined,
        products: productsArray,
      };

      const response = await requestService.createRequest(requestData);
      setSubmittedData(response.data);
      setIsSubmitted(true);
      toastSuccess("Request submitted successfully!");

      setTimeout(() => {
        navigate("/user/requests");
      }, 5000);
    } catch (err) {
      console.error("Submit error:", err);

      if (err.response?.status === 401) {
        setError("Your session has expired. Please log in again.");
        toastError("Session expired. Please log in again.");
        setTimeout(() => {
          navigate("/user/login", { state: { from: location.pathname } });
        }, 2000);
      } else {
        const errorMsg =
          err.response?.data?.message ||
          err.message ||
          "Failed to submit request";
        setError(errorMsg);
        toastError(errorMsg);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(categoryId);
    toastInfo(`${categories.find((c) => c.id === categoryId)?.name} selected`);
    setTimeout(() => {
      setCurrentStep(2);
    }, 300);
  };

  const addProduct = () => {
    if (!selectedCategory) {
      toastError("Please select a category first");
      return;
    }
    setFormData({
      ...formData,
      products: [
        ...formData.products,
        {
          id: null,
          name: "",
          sku: "",
          notes: "",
        },
      ],
    });
    setSelectedProductIndex(formData.products.length);
    setShowProductDropdown(true);
  };

  const selectProduct = (index, product) => {
    const updatedProducts = [...formData.products];
    updatedProducts[index] = {
      ...updatedProducts[index],
      id: product.id,
      name: product.name,
      sku: product.sku,
      mainImagePath: product.mainImagePath,
      imageBase64: product.imageBase64,
      galleryImages: product.galleryImages,
      image: product.image,
      imageAlt: product.imageAlt,
      category: product.category,
      notes: updatedProducts[index].notes || "",
    };
    setFormData({ ...formData, products: updatedProducts });
    setShowProductDropdown(false);
    setSelectedProductIndex(null);
    setProductSearch("");
    toastSuccess(`Added "${product.name}" to request`);
  };

  const updateProductNotes = (index, notes) => {
    const updatedProducts = [...formData.products];
    updatedProducts[index] = {
      ...updatedProducts[index],
      notes: notes,
    };
    setFormData({ ...formData, products: updatedProducts });
  };

  const removeProduct = (index) => {
    const productName = formData.products[index].name;
    const updatedProducts = formData.products.filter((_, i) => i !== index);
    setFormData({ ...formData, products: updatedProducts });
    toastInfo(`Removed "${productName || "product"}" from request`);
  };

  const getTotalProducts = () => {
    return formData.products.length;
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        return selectedCategory !== null;
      case 2:
        return (
          formData.products.length > 0 && formData.products.every((p) => p.id)
        );
      case 3:
        return formData.message.trim() !== "";
      default:
        return false;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const filteredProducts = (categoryProducts || []).filter(
    (p) =>
      p.name.toLowerCase().includes(productSearch.toLowerCase()) ||
      p.sku.toLowerCase().includes(productSearch.toLowerCase()),
  );

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  // Check if email is verified
  const isEmailVerified =
    user?.emailVerifiedAt !== null && user?.emailVerifiedAt !== undefined;

  // Show email verification overlay if not verified
  if (!isEmailVerified) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 flex items-center justify-center py-12 px-4">
        <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
          <div className="absolute -top-[40%] -left-[20%] w-[80%] h-[80%] bg-gradient-to-br from-yellow-200/20 to-orange-200/20 dark:from-yellow-900/20 dark:to-orange-900/20 rounded-full blur-3xl animate-pulse" />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full relative z-10"
        >
          <div className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-200/50 dark:border-gray-800/50 overflow-hidden">
            <div className="p-6 sm:p-8 bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border-b border-gray-100 dark:border-gray-800 text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full mb-4 shadow-lg">
                <Mail size={32} className="text-white" />
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Verify Your Email
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                We've sent a verification link to your email
              </p>
            </div>

            <div className="p-6 sm:p-8">
              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 mb-6">
                <p className="text-sm text-gray-700 dark:text-gray-300 text-center">
                  <span className="font-semibold">{user?.email}</span>
                </p>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                  <AlertCircle
                    size={14}
                    className="text-yellow-500 flex-shrink-0"
                  />
                  <span>Check your inbox and spam folder</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                  <Clock size={14} className="text-yellow-500 flex-shrink-0" />
                  <span>Verification link expires in 24 hours</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                  <ShieldCheck
                    size={14}
                    className="text-yellow-500 flex-shrink-0"
                  />
                  <span>Required to access all features</span>
                </div>
              </div>

              <button
                onClick={handleResendVerification}
                disabled={isResendingVerification}
                className="w-full py-3 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {isResendingVerification ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <RefreshCw size={18} />
                )}
                Resend Verification Email
              </button>

              <button
                onClick={() => {
                  localStorage.removeItem("accessToken");
                  localStorage.removeItem("userToken");
                  localStorage.removeItem("refreshToken");
                  localStorage.removeItem("user");
                  navigate("/user/login");
                }}
                className="w-full mt-3 py-3 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 font-medium rounded-xl transition-all hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center justify-center gap-2"
              >
                <ArrowLeft size={18} />
                Back to Login
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 border border-gray-200 dark:border-gray-900 rounded-2xl shadow-lg py-8 sm:py-12">
      {/* Blur Background Layers */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-[40%] -left-[20%] w-[80%] h-[80%] bg-gradient-to-br from-primary-200/20 to-secondary-200/20 dark:from-primary-900/20 dark:to-secondary-900/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute -bottom-[40%] -right-[20%] w-[80%] h-[80%] bg-gradient-to-tl from-primary-300/10 to-secondary-300/10 dark:from-primary-800/10 dark:to-secondary-800/10 rounded-full blur-3xl animate-pulse delay-1000" />
      </div>

      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-8 sm:mb-12"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl rounded-full border border-white/30 dark:border-gray-800/30 shadow-sm mb-4">
            <ShoppingBag className="w-3 h-3 sm:w-4 sm:h-4 text-primary-500" />
            <span className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">
              Wholesale Request
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            <span className="text-gray-900 dark:text-white">Request</span>
            <br />
            <span className="bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
              Inquiry
            </span>
          </h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Submit your request and our team will get back to you within{" "}
            <span className="text-primary-600 font-semibold">24 hours</span>{" "}
            with pricing and availability.
          </p>
        </motion.div>

        {/* User Info Bar */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 p-4 bg-gradient-to-r from-primary-50/50 to-secondary-50/50 dark:from-primary-900/20 dark:to-secondary-900/20 rounded-xl border border-primary-100 dark:border-primary-800/30"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center">
              <User className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Submitting as
              </p>
              <p className="font-medium text-gray-900 dark:text-white">
                {formData.name || user?.email?.split("@")[0] || "Guest"} •{" "}
                {formData.email}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Main Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl rounded-2xl sm:rounded-3xl border border-white/30 dark:border-gray-800/30 shadow-xl overflow-hidden"
        >
          <AnimatePresence mode="wait">
            {isSubmitted ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="p-8 sm:p-12 text-center"
              >
                <div className="flex justify-center mb-6">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 200, damping: 15 }}
                    className="relative"
                  >
                    <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center shadow-lg">
                      <CheckCircle className="w-10 h-10 text-white" />
                    </div>
                  </motion.div>
                </div>

                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-3">
                  Request Submitted!
                </h2>

                <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
                  Thank you for your interest. Our sales team will contact you
                  within 24 hours with detailed pricing and availability.
                </p>

                {submittedData && submittedData.requestNumber && (
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary-50 dark:bg-primary-900/20 rounded-full mb-6">
                    <FileText className="w-4 h-4 text-primary-600" />
                    <span className="text-sm font-medium text-primary-600">
                      Request #{submittedData.requestNumber}
                    </span>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <button
                    onClick={() => navigate("/user/requests")}
                    className="px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-semibold transition-all"
                  >
                    View My Requests
                  </button>
                  <button
                    onClick={() => navigate("/")}
                    className="px-6 py-3 bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl border border-white/30 dark:border-gray-800/30 text-gray-700 dark:text-gray-300 rounded-xl font-semibold hover:bg-white/60 transition-all"
                  >
                    Return to Homepage
                  </button>
                </div>
              </motion.div>
            ) : (
              <motion.form
                key="form"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onSubmit={handleSubmit}
              >
                {/* Progress Steps */}
                <div className="px-6 sm:px-8 pt-8 sm:pt-10 pb-6 border-b border-gray-200/50 dark:border-gray-800/50 bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl">
                  <div className="relative">
                    <div className="absolute left-24 right-24 top-8 h-[2px] bg-gray-200 dark:bg-gray-800 rounded-full" />
                    <div
                      className="absolute left-0 top-8 h-[2px] bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full transition-all duration-500 ease-out"
                      style={{ width: `${((currentStep - 1) / 2) * 100}%` }}
                    />
                    <div className="relative flex justify-between">
                      {[
                        { step: 1, label: "Category", icon: FolderTree },
                        { step: 2, label: "Products", icon: Package },
                        { step: 3, label: "Review", icon: FileText },
                      ].map(({ step, label, icon: Icon }) => {
                        const isCompleted = currentStep > step;
                        const isActive = currentStep === step;

                        return (
                          <div
                            key={step}
                            className="flex flex-col items-center"
                            style={{ flex: 1 }}
                          >
                            <div className="relative">
                              {isActive && (
                                <div className="absolute inset-0 rounded-full bg-primary-500/30" />
                              )}
                              <div
                                className={`relative w-16 h-16 rounded-2xl flex items-center justify-center transition-all duration-500 transform ${
                                  isCompleted
                                    ? "bg-gradient-to-br from-primary-500 to-secondary-500 text-white shadow-lg scale-100"
                                    : isActive
                                      ? "bg-gradient-to-br from-primary-500 to-secondary-500 text-white shadow-lg shadow-primary-500/30 scale-105 ring-4 ring-primary-500/20"
                                      : "bg-gray-100 dark:bg-gray-800 text-gray-400 scale-95"
                                }`}
                              >
                                {isCompleted ? (
                                  <Check className="w-6 h-6" />
                                ) : (
                                  <Icon className="w-6 h-6" />
                                )}
                              </div>
                            </div>
                            <span
                              className={`text-sm font-semibold mt-3 transition-all duration-300 ${
                                isCompleted || isActive
                                  ? "text-gray-900 dark:text-white"
                                  : "text-gray-400 dark:text-gray-600"
                              }`}
                            >
                              {label}
                            </span>
                            <div
                              className={`text-[10px] font-medium mt-1 px-2 py-0.5 rounded-full transition-all duration-300 ${
                                isCompleted || isActive
                                  ? "bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300"
                                  : "bg-gray-100 dark:bg-gray-800 text-gray-400"
                              }`}
                            >
                              Step {step} of 3
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="p-6 sm:p-8">
                  {/* Error Message */}
                  <AnimatePresence>
                    {error && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl flex items-center gap-3"
                      >
                        <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
                        <p className="text-sm text-red-600 dark:text-red-400">
                          {error}
                        </p>
                        <button
                          type="button"
                          onClick={() => setError(null)}
                          className="ml-auto p-1 hover:bg-red-100 dark:hover:bg-red-900/40 rounded-lg transition-colors"
                        >
                          <X
                            size={16}
                            className="text-red-600 dark:text-red-400"
                          />
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Step 1: Category Selection */}
                  {currentStep === 1 && (
                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                    >
                      <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-6">
                        Select Product Category
                      </h2>
                      <p className="text-gray-600 dark:text-gray-400 mb-6">
                        Choose the category that best matches the products
                        you're interested in.
                      </p>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        {categories.map((category) => {
                          const Icon = category.icon;
                          const isSelected = selectedCategory === category.id;
                          return (
                            <button
                              key={category.id}
                              type="button"
                              onClick={() => handleCategorySelect(category.id)}
                              className={`relative p-6 rounded-xl transition-all text-left group ${
                                isSelected
                                  ? "bg-gradient-to-r from-primary-500/10 to-secondary-500/10 border-2 border-primary-500 shadow-lg ring-2 ring-primary-500/20"
                                  : "bg-white/50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 hover:border-primary-400 hover:shadow-md"
                              }`}
                            >
                              {isSelected && (
                                <div className="absolute top-3 right-3">
                                  <CheckCircle className="w-5 h-5 text-primary-500" />
                                </div>
                              )}
                              <div
                                className={`w-14 h-14 rounded-xl bg-gradient-to-r ${category.gradient} flex items-center justify-center mb-4 shadow-md`}
                              >
                                <Icon className="w-7 h-7 text-white" />
                              </div>
                              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                {category.name}
                              </h3>
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                {category.description}
                              </p>
                            </button>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}

                  {/* Step 2: Products */}
                  {currentStep === 2 && (
                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                    >
                      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
                        <div>
                          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                            Select Products
                          </h2>
                          <p className="text-sm text-gray-500 mt-1">
                            Category:{" "}
                            <span className="font-medium text-primary-600">
                              {
                                categories.find(
                                  (c) => c.id === selectedCategory,
                                )?.name
                              }
                            </span>
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={addProduct}
                          className="flex items-center gap-2 px-4 py-2 bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 rounded-xl hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors"
                        >
                          <Plus size={18} />
                          <span className="text-sm font-medium">
                            Add Product
                          </span>
                        </button>
                      </div>

                      {productsLoading || categoryLoading ? (
                        <div className="flex items-center justify-center py-12">
                          <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
                        </div>
                      ) : formData.products.length === 0 ? (
                        <div className="text-center py-12 bg-gray-50/50 dark:bg-gray-800/30 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700">
                          <Package className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                          <p className="text-gray-500 dark:text-gray-400 mb-4">
                            No products added yet
                          </p>
                          <button
                            type="button"
                            onClick={addProduct}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors"
                          >
                            <Plus size={18} />
                            <span>Add Your First Product</span>
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {formData.products.map((product, index) => (
                            <motion.div
                              key={index}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -10 }}
                              className="p-4 bg-gray-50/50 dark:bg-gray-800/30 rounded-xl border border-gray-200 dark:border-gray-700"
                            >
                              <div className="flex flex-col gap-4">
                                <div className="relative">
                                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                                    Select Product
                                  </label>
                                  <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <input
                                      type="text"
                                      value={
                                        selectedProductIndex === index
                                          ? productSearch
                                          : product.name
                                      }
                                      onFocus={() => {
                                        setSelectedProductIndex(index);
                                        setShowProductDropdown(true);
                                      }}
                                      onChange={(e) => {
                                        setProductSearch(e.target.value);
                                        setShowProductDropdown(true);
                                      }}
                                      placeholder="Search for a product..."
                                      className="w-full pl-10 pr-3 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                                    />
                                    {showProductDropdown &&
                                      selectedProductIndex === index &&
                                      filteredProducts.length > 0 && (
                                        <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto">
                                          {filteredProducts.map((p) => (
                                            <ProductCard
                                              key={p.id}
                                              product={p}
                                              onSelect={(selectedProduct) =>
                                                selectProduct(
                                                  index,
                                                  selectedProduct,
                                                )
                                              }
                                            />
                                          ))}
                                        </div>
                                      )}
                                  </div>
                                </div>

                                {product.id && (
                                  <div>
                                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                                      Special Requirements / Notes
                                    </label>
                                    <textarea
                                      value={product.notes || ""}
                                      onChange={(e) =>
                                        updateProductNotes(
                                          index,
                                          e.target.value,
                                        )
                                      }
                                      rows={2}
                                      className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm resize-none"
                                      placeholder="Any specific requirements for this product?"
                                    />
                                  </div>
                                )}

                                <div className="flex justify-end">
                                  <button
                                    type="button"
                                    onClick={() => removeProduct(index)}
                                    className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                              </div>
                            </motion.div>
                          ))}

                          <div className="mt-6 p-4 bg-gradient-to-r from-primary-50 to-secondary-50 dark:from-primary-900/20 dark:to-secondary-900/20 rounded-xl">
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-gray-600 dark:text-gray-400">
                                Total Products:
                              </span>
                              <span className="font-bold text-gray-900 dark:text-white">
                                {getTotalProducts()}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="flex items-center justify-between mt-8">
                        <button
                          type="button"
                          onClick={prevStep}
                          className="px-5 py-2.5 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 font-medium rounded-xl transition-all flex items-center gap-2"
                        >
                          <ArrowLeft size={18} />
                          Back to Categories
                        </button>
                        <button
                          type="button"
                          onClick={nextStep}
                          disabled={!validateStep(2)}
                          className="px-6 py-2.5 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-xl transition-all flex items-center gap-2 shadow-lg"
                        >
                          Review & Submit
                          <ChevronRight size={18} />
                        </button>
                      </div>
                    </motion.div>
                  )}

                  {/* Step 3: Review & Submit */}
                  {currentStep === 3 && (
                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                    >
                      <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-6">
                        Review Your Request
                      </h2>

                      <div className="mb-6 p-4 bg-gray-50/50 dark:bg-gray-800/30 rounded-xl">
                        <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                          <User size={16} className="text-primary-500" />
                          Contact Information
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                          <div>
                            <span className="text-gray-500">Name:</span>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {formData.name}
                            </p>
                          </div>
                          <div>
                            <span className="text-gray-500">Email:</span>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {formData.email}
                            </p>
                          </div>
                          <div>
                            <span className="text-gray-500">Phone:</span>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {formData.phone || "Not provided"}
                            </p>
                          </div>
                          <div>
                            <span className="text-gray-500">Company:</span>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {formData.company || "Not specified"}
                            </p>
                          </div>
                          <div>
                            <span className="text-gray-500">Country:</span>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {formData.country || "Not specified"}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="mb-6 p-4 bg-gray-50/50 dark:bg-gray-800/30 rounded-xl">
                        <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                          <FolderTree size={16} className="text-primary-500" />
                          Category
                        </h3>
                        <p className="text-sm text-gray-900 dark:text-white">
                          {
                            categories.find((c) => c.id === selectedCategory)
                              ?.name
                          }
                        </p>
                      </div>

                      <div className="mb-6 p-4 bg-gray-50/50 dark:bg-gray-800/30 rounded-xl">
                        <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                          <Package size={16} className="text-primary-500" />
                          Products ({getTotalProducts()})
                        </h3>
                        <div className="space-y-3">
                          {formData.products.map((product, i) => (
                            <div
                              key={i}
                              className="pb-2 border-b border-gray-200 dark:border-gray-700 last:border-0"
                            >
                              <div className="flex items-start gap-3">
                                <div className="w-12 h-12 rounded-lg bg-gray-100 dark:bg-gray-800 overflow-hidden flex-shrink-0">
                                  <ProductImage
                                    product={product}
                                    className="w-full h-full"
                                  />
                                </div>
                                <div className="flex-1">
                                  <p className="font-medium text-gray-900 dark:text-white">
                                    {product.name || `Product ${i + 1}`}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    SKU: {product.sku || "N/A"} • Category:{" "}
                                    {
                                      categories.find(
                                        (c) => c.id === selectedCategory,
                                      )?.name
                                    }
                                  </p>
                                  {product.notes && (
                                    <p className="text-xs text-gray-500 mt-1 italic">
                                      Notes: {product.notes}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="mb-6">
                        <label
                          htmlFor="request-message"
                          className="block text-sm font-semibold text-gray-900 dark:text-white mb-2"
                        >
                          Message <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <textarea
                            id="request-message"
                            value={formData.message}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                message: e.target.value,
                              })
                            }
                            rows={4}
                            placeholder="Describe your requirements, special instructions, or any additional information..."
                            className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm resize-none transition-all placeholder:text-gray-400 dark:placeholder:text-gray-500"
                            required
                          />
                        </div>
                        {!formData.message.trim() && (
                          <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                            <AlertCircle size={12} />A message is required to
                            submit your request
                          </p>
                        )}
                      </div>

                      <div className="flex items-center justify-between">
                        <button
                          type="button"
                          onClick={prevStep}
                          className="px-5 py-2.5 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 font-medium rounded-xl transition-all flex items-center gap-2"
                        >
                          <ArrowLeft size={18} />
                          Back to Products
                        </button>

                        <button
                          type="submit"
                          disabled={isLoading || !formData.message.trim()}
                          className="group relative px-8 py-3 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold rounded-xl transition-all flex items-center gap-3 shadow-lg shadow-primary-600/20"
                        >
                          {isLoading ? (
                            <>
                              <Loader2 size={20} className="animate-spin" />
                              <span>Submitting...</span>
                            </>
                          ) : (
                            <>
                              <Send size={20} />
                              <span>Submit Request</span>
                            </>
                          )}
                        </button>
                      </div>
                    </motion.div>
                  )}
                </div>
              </motion.form>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Help Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-6 text-center"
        >
          <p className="text-xs text-gray-500">
            Need help? Contact our support team at{" "}
            <a
              href="mailto:admin@tradebridge-export.com"
              className="text-primary-600 font-medium hover:underline"
            >
              admin@tradebridge-export.com
            </a>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default Inquiry;
